import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { LeadSummary } from '@tg-leads/contracts';
import { io, Socket } from 'socket.io-client';

import { getToken } from './api';

export interface LeadReplyEvent {
  leadId: string;
  conversationId: string;
  text: string;
  ts: string;
  assignedSalesId: string | null;
}

/**
 * Connect to the Socket.IO gateway and invalidate the leads query whenever
 * a new lead lands. The JWT is sent via `auth.token` in the handshake;
 * server-side gateway verifies + joins `tenant:{tid}` room.
 *
 * Optional callbacks:
 *   - onNewLead: a `lead:detected` event arrived
 *   - onReply: a `lead:reply` event arrived (target DM'd us back)
 *
 * Connection is created once per mount and torn down on unmount. Socket.IO
 * handles reconnects internally so we don't need to manage retry logic.
 */
export function useLeadRealtime(opts: {
  onNewLead?: (lead: LeadSummary) => void;
  onReply?: (reply: LeadReplyEvent) => void;
} = {}): { connected: boolean } {
  const qc = useQueryClient();
  const sockRef = useRef<Socket | null>(null);
  const connectedRef = useRef(false);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    // Connecting to same origin — socket.io-client autodetects via location.
    const sock = io({
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    sockRef.current = sock;

    sock.on('connect', () => {
      connectedRef.current = true;
    });
    sock.on('disconnect', () => {
      connectedRef.current = false;
    });
    sock.on('lead:detected', (msg: { lead: LeadSummary }) => {
      qc.invalidateQueries({ queryKey: ['sales', 'leads'] });
      qc.invalidateQueries({ queryKey: ['leads'] });
      opts.onNewLead?.(msg.lead);
    });
    sock.on('lead:reply', (msg: LeadReplyEvent) => {
      qc.invalidateQueries({ queryKey: ['sales', 'leads'] });
      qc.invalidateQueries({ queryKey: ['sales', 'lead', msg.leadId] });
      qc.invalidateQueries({
        queryKey: ['sales', 'lead', msg.leadId, 'conversation'],
      });
      opts.onReply?.(msg);
    });

    return () => {
      sock.disconnect();
      sockRef.current = null;
    };
    // We deliberately only run once per mount — `opts` change tracking
    // would tear/rebuild the socket on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { connected: connectedRef.current };
}
